import { Fragment, useMemo, type FC } from 'react';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { LoadingPage } from '#packages/web/layouts/page/templates';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';

import { type AuditDetailsPageViewModel } from './audit-details-page.view-model';
import {
  AuditDetailsPageRenderer,
  type AuditDetailsPageLinks,
} from './audit-details-page.renderer';
import { AuditDetailsSyncDialog } from './audit-details-sync-dialog';

export type AuditDetailsPageContainerProps =
  ViewModelProps<AuditDetailsPageViewModel> & LoggingContext;

export const AuditDetailsPageContainer: FC<AuditDetailsPageContainerProps> = ({
  viewModel,
}) => {
  const {
    page,
    deleteDialog,
    syncState,
    syncDialog,
    resetSyncState,
    syncMutationState,
    isSyncSuccess,
    onDelete,
    onSyncConfirm,
  } = viewModel;

  const links = useMemo<AuditDetailsPageLinks | undefined>(() => {
    if (page.state !== 'success') {
      return undefined;
    }

    return {
      energyAudit: {
        background: routes.audit.getEnergyAuditBackgroundFormPath(page.data.id),
        waterFixture: routes.audit.getEnergyAuditWaterFixtureFormPath(
          page.data.id,
        ),
        lighting: routes.audit.getEnergyAuditLightingFormPath(page.data.id),
        lightingCount: routes.audit.getEnergyAuditLightingCountFormPath(
          page.data.id,
        ),
        mechanical: routes.audit.getEnergyAuditMechanicalFormPath(page.data.id),
        checklist: routes.audit.getEnergyAuditChecklistFormPath(page.data.id),
      },

      auditSettings: routes.audit.getSettingsPath(page.data.id),
    };
  }, [page]);

  return (
    <Fragment>
      {/* Loading */}
      {page.state === 'loading' && <LoadingPage />}

      {/* Success */}
      {page.state === 'success' && <AuditDetailsPageRenderer links={links!} />}

      {/* Error */}
      {page.state === 'error' && <ThrowError error={page.error} />}

      {/* Delete Dialog */}
      {deleteDialog.isOpen && (
        <DeleteConfirmationDialog
          open
          onCancel={deleteDialog.close}
          onConfirm={onDelete.bind(null, { confirmed: true })}
        />
      )}

      {/* Sync Dialog */}
      {syncDialog.isOpen && syncDialog.data && (
        <AuditDetailsSyncDialog
          audit={syncDialog.data}
          syncState={syncState}
          syncMutationState={syncMutationState}
          isSyncSuccess={isSyncSuccess}
          resetSyncState={resetSyncState}
          onClose={syncDialog.close}
          onSync={onSyncConfirm}
        />
      )}
    </Fragment>
  );
};
